import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Slide,
  Alert,
  Modal,
  useMediaQuery,
  useTheme,
  Paper,
  InputAdornment,
} from "@mui/material";
import {
  CommonFieldsData,
  generalResearch,
  doctor,
  locations,
  performance,
  research,
  specialities,
  typology,
} from "../../networking";
import {
  Doctor,
  Specialty,
  Typology,
  Location,
  Performance,
  AppointmentData,
} from "../../services/interfaces";
import "../../css/appointmentManagement.css";
import Calendar from "./Calendar/Calendar";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { DataContext } from "./Calendar/Context/ContextWrapper";
import {
  AppointmentsByPACAPRE,
  formatHour,
} from "../AppointmentResearch/AppointmentResearch";
import { Patient } from "./Calendar/ModalReservation/ModalReservation";
import GestioneSospesi from "./GestioneSospesi/GestioneSospesi";
import { FormatGrigliaOrariPerGestioneSospesi } from "../../services/gestioneSospesi";
import AppointmentManagementDetail from "./AppointmentManagementDetail/AppointmentManagementDetail";

//#region DATA TYPES
interface Appointment {
  durataPrestazione: string;
  agenda: any;
  prestazione: any;
  specialita: any;
  tipoAgenda: any;
  data: string;
  orari: string;
  durata: string;
  codice: string;
  primaDisponibilita: string;
  tipologiaAgenda: string;
  descrizionePrestazione: string;
  descrizioneSpecialita: string;
  descrizioneTipoAgenda: string;
  noteGiornaliere?: string;
  notaMedico?: string;
  flagOrarioGiornaliero?: string;
}

interface Agenda {
  nominativoMedico: string;
  genere: string;
  sede: string;
  sedeIndirizzo: string;
  struttura: string;
  tariffa: string;
  appuntamenti: Appointment[];
  codice: string;
  primaDisponibilita: string;
  tipoAgenda: string;
  durataPrestazione: string;
  noteSettimana?: string;
  notaMedico?: string;
  flagOrarioGiornaliero?: string;
}

type Agende = {
  [medicoId: string]: {
    [tipoAgenda: string]: Agenda;
  };
};

export type ModalitaSospesi = { state: boolean; view: boolean };
export interface Enti {
  pkey: string;
  pkeyf: string;
  pkeyl: string;
  codice: string;
  codicepostic: string;
  "ragione sociale": string;
}
export interface PosizioneTicket {
  pkey: string;
  pkeyf: string;
  codice: string;
  "inizio validita": string;
  "fine validita": string;
  descrizione: string;
  "descrizione tipo calcolo": string;
  indice: string;
  "tipo calcolo": string;
  "proposto in accettazione": string;
  CODICEPOSTIC?: string;
  RICERCAPOSTIC?: string;
  TIPOCALCOLO?: string;
  "descrizione regime"?: string;
}
export interface ClassePriorita {
  pkey: string;
  pkeyf: string;
  pkeyl: string;
  codice: string;
  "data inizio validit": string;
  "data fine validit": string;
  descrizione: string;
}
//#endregion
const today = dayjs().format("YYYY-MM-DD");

// logica per tenere traccia di un id per la gestione alternanza
// delle immagini generate
let idCounter = 0;

function generateId() {
  return ++idCounter;
}

// componente localizzata per gestire controllo di quale appuntamento è in fase di modifica
function CustomTypography(props: {
  idx: number;
  appointment: any;
  appointmentDateToUpdateData: any | undefined;
}) {
  const selectedAppointment =
    props.appointment.data === props.appointmentDateToUpdateData?.DATAESAME &&
    props.appointment.ora ===
      formatHour(props.appointmentDateToUpdateData?.ORAESAME) &&
    props.appointment.descrizionePrestazione ===
      props.appointmentDateToUpdateData?.DESCRESAME;

  return (
    <Box
      key={props.idx}
      sx={{
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          display: "inline-block",
          color: selectedAppointment ? "#D32F2F" : "#01579B",
          marginRight: 1,
        }}
      >
        <strong>Data</strong>
      </Typography>
      <Typography
        sx={{
          display: "inline-block",
          fontSize: "0.9rem",
          color: selectedAppointment ? "#E65100" : "",
        }}
      >
        {dayjs(props.appointment.data).format("DD/MM/YY")}
      </Typography>
      <Typography
        sx={{
          display: "inline-block",
          marginLeft: 2,
          color: selectedAppointment ? "#D32F2F" : "#01579B",
          marginRight: 1,
        }}
      >
        <strong>Ore</strong>
      </Typography>
      <Typography
        sx={{
          display: "inline-block",
          fontSize: "0.9rem",
          color: selectedAppointment ? "#E65100" : "",
        }}
      >
        {props.appointment.ora}
      </Typography>
      <Typography
        sx={{
          display: "inline-block",
          marginLeft: 2,
          marginRight: 1,
          color: selectedAppointment ? "#D32F2F" : "#01579B",
        }}
      >
        <strong>Durata</strong>
      </Typography>
      <Typography
        sx={{
          display: "inline-block",
          fontSize: "0.9rem",
          color: selectedAppointment ? "#E65100" : "",
        }}
      >
        {props.appointment.durata} minuti
      </Typography>
    </Box>
  );
}

export function NotifyCancelAppointment(props: {
  message: string;
  cancellationNotification: boolean;
  cancellationModalTimer: boolean;
}) {
  return (
    <Modal open={props.cancellationModalTimer}>
      <Slide in={props.cancellationNotification} direction="down" timeout={800}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <Alert
            severity={
              props.message === "L'inserimento è stato annullato"
                ? "success"
                : "warning"
            }
            sx={{
              maxWidth: "150vh",
              width: "90%",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              justifyItems: "center",
              transform: "translate3d(20%, 100%, 0%)",
              flexDirection: "row",
              "& .MuiAlert-icon": {
                fontSize: 30,
              },
            }}
          >
            <Typography
              component="div"
              marginTop={"20px"}
              marginLeft={"20px"}
              marginRight={"10px"}
              marginBottom={"20px"}
              fontSize={"20px"}
              fontStyle={"capitalize"}
            >
              {props.message}
            </Typography>
          </Alert>
        </Box>
      </Slide>
    </Modal>
  );
}

// componente finestra di Alert durante la gestione dei sospesi
const AlertGestioneSospesi = ({
  setModalitaSospesi,
}: {
  setModalitaSospesi: React.Dispatch<React.SetStateAction<ModalitaSospesi>>;
}) => {
  return (
    <Box>
      <Alert
        severity="info"
        sx={{
          "& .MuiAlert-icon": {
            fontSize: 30,
          },
        }}
        action={
          <Button
            variant="outlinedError"
            size="small"
            onClick={() => setModalitaSospesi({ view: false, state: false })}
          >
            Uscita
          </Button>
        }
      >
        <Typography
          component="div"
          fontWeight={"bold"}
          fontSize={"20px"}
          fontStyle={"capitalize"}
        >
          {`Sei in modalità gestione sospesi`}
        </Typography>
      </Alert>
    </Box>
  );
};

//#region Funzioni Fetch valorizzano le Set Enti, Impegnativa e Priorita
// Fetch valorizza option select degli enti
const fetchEntiData = async () => {
  const objectJsonRequest: CommonFieldsData = {
    tabella: "ENTI",
    codice: "",
    filtro: "",
    applicazione: "",
    attivi: "1",
  };

  try {
    const objectJsonResponse = await generalResearch(objectJsonRequest);
    const dataEnti = (objectJsonResponse.data?.dati ?? []).map(
      (e: Enti, index: number) => ({
        ...e,
        indice: index,
      })
    );

    const dataPosizioneTicketsToSort: Enti[] = dataEnti;

    return dataPosizioneTicketsToSort.sort((a, b) => {
      if (a["ragione sociale"] > b["ragione sociale"]) {
        return 1;
      }
      if (b["ragione sociale"] > a["ragione sociale"]) {
        return -1;
      }
      return 0;
    });
  } catch (error) {
    console.error("Errore nella fetch di ricerca degli enti", error);
    return [];
  }
};

// Fetch valorizza option select della posizione Ticket
const fetchPosizioneTicketData = async () => {
  const objectJsonRequest: CommonFieldsData = {
    tabella: "Postic",
    codice: "",
    filtro: "",
    applicazione: "",
    attivi: "1",
    "tipo calcolo": "",
  };

  try {
    const objectJsonResponse = await generalResearch(objectJsonRequest);

    const dataPosizioneTickets = objectJsonResponse.data.dati.map(
      (e: PosizioneTicket, index: number) => {
        return {
          ...e,
          indice: index,
        };
      }
    );

    const dataPosizioneTicketsToSort: PosizioneTicket[] = dataPosizioneTickets;

    return dataPosizioneTicketsToSort.sort((a, b) => {
      if (a.descrizione > b.descrizione) {
        return 1;
      }
      if (b.descrizione > a.descrizione) {
        return -1;
      }
      return 0;
    });
  } catch (error) {
    console.error("Errore nella fetch di ricerca delle posizioni ticket");
    return [];
  }
};

// Fetch valorizza option select della priorità classe
const fetchPrioritaClasseData = async () => {
  const objectJsonRequest: CommonFieldsData = {
    tabella: "clsprt",
    codice: "",
    filtro: "",
    applicazione: "",
    attivi: "1",
  };

  try {
    const objectJsonResponse = await generalResearch(objectJsonRequest);

    const dataClassiPriorita = objectJsonResponse.data.dati.map(
      (e: ClassePriorita, index: number) => {
        return {
          ...e,
          indice: index,
        };
      }
    );

    const dataClassiPrioritaToSort: ClassePriorita[] = dataClassiPriorita;

    dataClassiPrioritaToSort.sort((a, b) => {
      if (a.descrizione > b.descrizione) {
        return 1;
      }
      if (b.descrizione > a.descrizione) {
        return -1;
      }
      return 0;
    });

    return dataClassiPrioritaToSort;
  } catch (error) {
    console.error("Errore nella fetch di ricerca della classe priorità");
    return [];
  }
};
//#endregion

const AppointmentManagement = () => {
  const { handleSubmit, control, getValues, setValue, watch } =
    useForm<CommonFieldsData>({
      defaultValues: {
        tipoAgenda: "ALL",
        listino: "",
        specialita: "",
        prestazione: "",
        medico: [""],
        data: today,
        dataFine: "",
        giorni: 15,
        orari: "Tutto il Giorno",
        strutture: "",
      },
    });

  const context = useContext(DataContext);
  // console.log("DATA PAZIENTE", context?.pazienteData);

  // Funzione navigate per passare da una pagina ad un'altra
  const navigate = useNavigate();

  // Funzione per il passaggio dati dell'appuntamento da modificare
  const appointmentResearchRedirectAtConclusion = async () => {
    navigate("/research");
  };

  // Location per ricezione dei dati in caso di redirect e di modifica di un'appuntamento da appointmentResearch
  const location = useLocation();
  const [appointmentDateToUpdateData, setAppointmentDateToUpdateData] =
    useState<AppointmentData | undefined>(
      location.state?.appointmentData || undefined
    );
  const [appointmentsListData, setAppointmentsListData] = useState<
    AppointmentsByPACAPRE | undefined
  >(location.state?.appointmentsList || undefined);

  // Inizializzazione in caso di redirect e di modifica di data di un appuntamento in corso
  useEffect(() => {
    if (appointmentDateToUpdateData && appointmentsListData) {
      context?.setStatusAvanzamentoPrenotazione(3);
      setValue("tipoAgenda", appointmentDateToUpdateData.CODICETPGPRE || "ALL");
      setValue("specialita", appointmentDateToUpdateData.CODSPECIALITA);
      setValue("prestazione", appointmentDateToUpdateData.CODICEANAPRE);

      const tempPatient: Patient = {
        NOME: appointmentDateToUpdateData.NOMEPAZIENTE,
        COGNOME: appointmentDateToUpdateData.COGNOMEPAZIENTE,
        DATANASCPAZIENTE: appointmentDateToUpdateData.DATANASCITA,
        CODICEFISCALE: appointmentDateToUpdateData.DATANASCITA,
        TELEFONO: appointmentDateToUpdateData.TELEFONO1,
        EMAIL: "",
        IDPAZIENTE: "",
      };

      const formattedArray: any[] =
        appointmentsListData.GRUPPO_APPUNTAMENTI.map((e) => {
          const formattedObject = {
            nomePaziente: e.NOMEPAZIENTE,
            cognomePaziente: e.COGNOMEPAZIENTE,
            statoPrenotazione: e.STATOPRENOTAZIONE,
            descrizionePrestazione: e.DESCRESAME,
            nominativoMedico: e.NOMEMEDICO,
            data: e.DATAESAME,
            ora: formatHour(e.ORAESAME),
            durata: e.DURATAESAME,
          };
          return formattedObject;
        });

      context?.setAppointmentsList(formattedArray);

      context?.setPazienteData(tempPatient);
      context?.setIdsGenerati({
        IdPacata: appointmentDateToUpdateData.IDPACATE,
        IdPacapr: appointmentDateToUpdateData.IDPACAPR,
        IdPacada: "",
        idPacadaOld: appointmentDateToUpdateData.IDPACADA,
      });
    }
  }, []);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [doctorData, setDoctorData] = useState<Doctor[]>([]);
  const [specialitiesData, setSpecialitiesData] = useState<Specialty[]>([]);
  const [locationsData, setLocationsData] = useState<Location[]>([]);
  const [performanceData, setPerformanceData] = useState<Performance[]>([]);
  const [typologyData, setTypologyData] = useState<Typology[]>([]);
  const [calendarData, setCalendarData] = useState<Agende>({});
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingSpinner, setLoadingSpinner] = useState<boolean>(false);
  const [searchSubmit, setSearchSubmit] = useState<boolean>(false);
  const [loadingDataInputs, setLoadingDataInputs] = useState<boolean>(true);
  const [accordion, setAccordion] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<string>(today);
  const [daysForCalendar, setDaysForCalendar] = useState(15);
  const [daysInput, setDaysInput] = useState(15);
  const [firstDateAvailable, setFirstDateAvailable] = useState<string>("");
  const [selectedPrestazione, setSelectedPrestazione] =
    useState<Performance | null>(null);

  // Messaggio restituito, passato come parametro nella funzione che lo gestisce
  const [message, setMessage] = React.useState("default");
  const [dateError, setDateError] = React.useState(false);

  // State di gestione modalità sospesi
  const [modalitaSospesi, setModalitaSospesi] = useState<ModalitaSospesi>({
    state: false,
    view: false,
  });

  // Dati per generare la tabella degli orari al click di un evento giornaliero durante la modalità sospesi
  const [listaOrariDettaglio, setListaOrariDettaglio] = useState<
    FormatGrigliaOrariPerGestioneSospesi[]
  >([]);

  //#region Logica di controllo del SID / Token
  // Stato per indicare se il token è disponibile
  const [isTokenAvailable, setIsTokenAvailable] = useState(false);
  const [sidAvailable, setSidAvailable] = useState(false); // Stato per tracciare il completamento del recupero del SID

  //Per gestire la visualizzazione lato smartphone
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Con AuthGuard, l'utente e' gia' autenticato quando raggiunge questa pagina
  useEffect(() => {
    setSidAvailable(true);
  }, []);
  //#endregion

  //Logica valorizzazione delle selects nella componente figlia della *AcceptanceStep*
  const [rowsEntiData, setRowsEntiData] = useState<Enti[]>([]);
  const [rowsPosizioneTicketData, setRowsPosizioneTicketData] = useState<
    PosizioneTicket[]
  >([]);
  const [rowsClassePrioritaData, setRowsClassePrioritaData] = useState<
    ClassePriorita[]
  >([]);

  // UseEffect per una silent fetch che serve a valorizzare gli enti, posizione ticket e priorità ticket per Modal Reservation
  useEffect(() => {
    const fetchData = async () => {
      const entiData = await fetchEntiData();
      if (entiData) {
        setRowsEntiData(entiData);
      }

      const posizioneTicketData = await fetchPosizioneTicketData();
      if (posizioneTicketData) {
        setRowsPosizioneTicketData(posizioneTicketData);
      }

      const prioritaClasseData = await fetchPrioritaClasseData();
      if (prioritaClasseData) {
        setRowsClassePrioritaData(prioritaClasseData);
      }
    };

    fetchData();
  }, []);

  //#region Watchfields delle select
  const watchedFields = useWatch({
    control,
    name: [
      "tipoAgenda",
      "listino",
      "specialita",
      "prestazione",
      "medico",
      "dataInizio",
      "dataFine",
      "giorni",
      "orari",
    ],
  });

  // const medici = watch("medici");
  const medico = watch("medico");
  const specialita = watch("specialita");
  const prestazione = watch("prestazione");
  const tipoAgenda = watch("tipoAgenda");
  //#endregion

  //#region Logica apparizione / sparizione per notifica cancellazione appuntamento
  const [cancellationNotification, setCancellationNotification] =
    useState(false);
  const [cancellationModalTimer, setCancellationModalTimer] = useState(false);

  // Funzione Timer per animazione alert di notificazione
  function startTimer(duration: number, callback: () => void): void {
    setTimeout(() => {
      callback();
    }, duration);
  }

  const handleCancellationNotification = () => {
    // Timer di apparizione alert di cancellazione
    startTimer(4500, () => {
      setCancellationNotification(false);
    });

    // Timer di apparizione modale contenente l'alert
    startTimer(5000, () => {
      setCancellationModalTimer(false);
    });
  };
  //#endregion

  useEffect(() => {
    if (!sidAvailable) return; // Esegui solo se il SID è disponibile

    const updateFiltersAndFetch = async () => {
      const updatedFilters: CommonFieldsData = {
        tipoAgenda: watchedFields[0] || "",
        listino: watchedFields[1] || "",
        specialita: watchedFields[2] || "",
        prestazione: watchedFields[3] || "",
        medico: watchedFields[4] || "",
        dataInizio: watchedFields[5] || "",
        giorni: watchedFields[7] || 1,
        orari: watchedFields[8] || "",
      };

      const tipoAgendaRequest: CommonFieldsData = {
        listino: "",
        specialita: updatedFilters.specialita,
        prestazione: updatedFilters.prestazione,
        medico: updatedFilters.medico,
      };

      const specialitaRequest: CommonFieldsData = {
        listino: "",
        tipoAgenda: updatedFilters.tipoAgenda,
        medico: updatedFilters.medico,
        prestazione: updatedFilters.prestazione,
      };

      const prestazioniRequest: CommonFieldsData = {
        listino: "",
        tipoAgenda: updatedFilters.tipoAgenda,
        specialita: updatedFilters.specialita,
        medico: updatedFilters.medico,
      };

      const mediciRequest: CommonFieldsData = {
        listino: "",
        tipoAgenda: updatedFilters.tipoAgenda,
        specialita: updatedFilters.specialita,
        prestazione: updatedFilters.prestazione,
        medico: [],
      };

      // Chiamata alle fetch che assegnano valori alle apposite response
      try {
        setLoadingDataInputs(true);
        const [
          doctorResponse,
          specialitiesResponse,
          locationsResponse,
          performanceResponse,
          typologyResponse,
        ] = await Promise.all([
          doctor(mediciRequest),
          specialities(specialitaRequest),
          locations(updatedFilters),
          performance(prestazioniRequest),
          typology(tipoAgendaRequest),
        ]);
        setLoadingDataInputs(false);

        const sortedSpecialities: Specialty[] = specialitiesResponse.data.dati;

        if (specialitiesResponse.data.dati) {
          sortedSpecialities.sort((a, b) => {
            if (a.DESCRIZIONE > b.DESCRIZIONE) {
              return 1;
            }
            if (b.DESCRIZIONE > a.DESCRIZIONE) {
              return -1;
            }
            return 0;
          });
        }

        const sortedDoctors: Doctor[] = doctorResponse.data.dati;

        if (doctorResponse.data.dati) {
          sortedDoctors.sort((a, b) => {
            if (a.DESCRIZIONE > b.DESCRIZIONE) {
              return 1;
            }
            if (b.DESCRIZIONE > a.DESCRIZIONE) {
              return -1;
            }
            return 0;
          });
        }

        const sortedPrestazioni: Performance[] = performanceResponse.data.dati;

        if (performanceResponse.data.dati) {
          sortedPrestazioni.sort((a, b) => {
            if (a.DESCRIZIONE > b.DESCRIZIONE) {
              return 1;
            }
            if (b.DESCRIZIONE > a.DESCRIZIONE) {
              return -1;
            }
            return 0;
          });
        }

        setTypologyData(typologyResponse.data.dati);

        setDoctorData(sortedDoctors);

        setSpecialitiesData(specialitiesResponse.data.dati);

        if (locationsResponse.data?.dati) {
          setLocationsData(
            Object.entries(locationsResponse.data.dati).map(([key, value]) => ({
              id: key,
              descrizione: (value as { DESCRIZIONE: string }).DESCRIZIONE,
            }))
          );
        }
        setPerformanceData(performanceResponse.data?.dati ?? []);

        // // Logica che impedisce la duplicazione dell'option ALL Tipo Agenda
        const all: Typology = { CODICE: "ALL", DESCRIZIONE: "Tutti" };
        const listaTipologie: Typology[] = [...(typologyResponse.data?.dati ?? [])];

        // //Verifica della presenza utilizzando una funzione di utilità per rendere il codice più leggibile
        const isPresent = listaTipologie.some((x) => x.CODICE === all.CODICE);

        if (!isPresent) {
          //   // Usa lo spread operator per mantenere l'immutabilità se necessario
          setTypologyData([all, ...listaTipologie]);
          if (!specialitiesData) {
          }
        } else {
          setTypologyData(listaTipologie);
        }
      } catch (error) {
        console.error("Errore durante il fetch dei dati:", error);
      }

      const fieldsRequired =
        updatedFilters.specialita?.trim() !== "" &&
        updatedFilters.prestazione?.trim() !== "";

      setIsDisabled(!fieldsRequired);
    };

    updateFiltersAndFetch();
  }, [sidAvailable, medico, prestazione, tipoAgenda, specialita]);

  // Funzione che verifica se la prestazione selezionata ha note associate
  const hasNote = (p?: Performance | null) =>
    typeof p?.NOTEPRENOTAZIONE === "string" &&
    p.NOTEPRENOTAZIONE.trim().length > 0;

  // Effetto che si attiva al variare della prestazione selezionata
  useEffect(() => {
    if (!prestazione || !performanceData?.length) {
      setSelectedPrestazione(null);
      return;
    }
    const obj =
      performanceData.find(
        (p) => String(p.PRESTAZIONE) === String(prestazione)
      ) ?? null;
    setSelectedPrestazione(obj);
  }, [performanceData, prestazione]);

  const handleDataSubmit = async (data: CommonFieldsData) => {
    // console.log("dati", data);
    setLoadingData(true);
    setDaysForCalendar(daysInput);

    try {
      const response = await research(data);

      // console.log("RESPONSE", response);

      const agende: Agende = {};

      context?.setApCardDettaglio({
        ablAPCARD: response.data?.ablAPCARD,
        titoloAPCARD: response.data?.titoloAPCARD,
      });

      const agendaList = Array.isArray(response.data?.elencoAgende?.agenda)
        ? response.data.elencoAgende.agenda
        : response.data?.elencoAgende?.agenda
        ? [response.data.elencoAgende.agenda]
        : [];

      agendaList.forEach((curr: any) => {
        // console.log("ELEMENTO", curr);

        const tipoAgenda =
          curr.codTipoAgenda !== "ALL" ? curr.codTipoAgenda : "";
        const codice = curr.codice || "unknown";
        const medicoId = curr.codiceMedico || "unknown";
        const agendaKey = `${tipoAgenda}-${codice}`;
        const durataPrestazione = curr.durataPrestazione;

        if (!agende[medicoId]) {
          agende[medicoId] = {};
        }

        if (!agende[medicoId][agendaKey]) {
          agende[medicoId][agendaKey] = {
            nominativoMedico: curr.nominativoMedico,
            genere: curr.genere,
            sede: curr.sede,
            sedeIndirizzo: curr.sedeIndirizzo,
            struttura: curr.struttura,
            tariffa: curr.tariffaPrestazione,
            appuntamenti: [],
            codice: curr.codice,
            primaDisponibilita: curr.primaDisponibilita,
            tipoAgenda: curr.tipoAgenda,
            durataPrestazione: curr.durataPrestazione,
            noteSettimana: curr.elencoNote,
            notaMedico: curr.notaMedico,
          };
        }

        const elencoAppuntamenti: any[] = curr.elencoAppuntamenti;

        elencoAppuntamenti.forEach((element: any) => {
          if (Array.isArray(element.appuntamento)) {
            element.appuntamento.forEach(
              (result: {
                durata: string;
                dataAppuntamento: string;
                oraAppuntamento: string;
                durataPrestazione: string;
                flagOrarioGiornaliero: string;
              }) => {
                const codiceSpecialita = getValues("specialita");
                const codicePrestazione = getValues("prestazione");
                const codiceTipoAgenda = getValues("tipoAgenda");

                // 🔎 Trova la descrizione corrispondente nei dati delle API
                const descrizioneSpecialita =
                  specialitiesData.find(
                    (s) => String(s.SPECIALITA) === String(codiceSpecialita)
                  )?.DESCRIZIONE || "";

                const descrizionePrestazione =
                  performanceData.find(
                    (p) => String(p.PRESTAZIONE) === String(codicePrestazione)
                  )?.DESCRIZIONE || "";

                const descrizioneTipoAgenda =
                  typologyData.find(
                    (t) => String(t.CODICE) === String(codiceTipoAgenda)
                  )?.DESCRIZIONE || "";

                agende[medicoId][agendaKey].appuntamenti.push({
                  data: result.dataAppuntamento,
                  orari: result.oraAppuntamento,
                  durata: result.durata,
                  prestazione: data.prestazione,
                  specialita: data.specialita,
                  tipoAgenda: curr.codTipoAgenda,
                  agenda: data.agenda,
                  codice: codice,
                  primaDisponibilita: curr.primaDisponibilita,
                  tipologiaAgenda: curr.tipoAgenda,
                  durataPrestazione: durataPrestazione,
                  descrizionePrestazione,
                  descrizioneSpecialita,
                  descrizioneTipoAgenda,
                  flagOrarioGiornaliero: result.flagOrarioGiornaliero,
                });
              }
            );
          }
        });

        const primoAppuntamento = agendaList.flatMap((item: any) => {
          return item.elencoAppuntamenti.flatMap((data: any) => {
            const appuntamenti = Array.isArray(data.appuntamento)
              ? data.appuntamento
              : data.appuntamento
              ? [data.appuntamento]
              : [];

            return appuntamenti.map((result: any) => result.dataAppuntamento);
          });
        });

        const minDate = primoAppuntamento.reduce((min: any, current: any) => {
          const currentDate = dayjs(current, "YYYYMMDD");
          const minDate = dayjs(min, "YYYYMMDD");
          return currentDate.isBefore(minDate) ? current : min;
        }, "99991231");

        setFirstDateAvailable(minDate);
      });

      console.log("Organized Data by agenda type and medico:", agende);

      setCalendarData(agende);

      setTimeout(() => {
        setLoadingData(false);
        if (!agende) {
          setSearchSubmit(true);
        }
      }, 3000);
    } catch (error) {
      console.error("Error during form submission:", error);
      setLoadingData(false);
    }
  };

  const handleResetFields = () => {
    control._reset();
    setSearchSubmit(false);
  };

  const handleResetForm = () => {
    control._reset();
    setAccordion(true);
    setAppointmentDateToUpdateData(undefined);
    context?.setNumeroAppuntamento("");
    context?.setPazienteData(undefined);
    context?.setStatusAvanzamentoPrenotazione(0);
    context?.setIdsGenerati(undefined);
    context?.setTipoAgendaLock("");
    context?.clearAppointmentsList();
    context?.setTimeExpiry(null);
    setSearchSubmit(false);

    if (context?.statusAvanzamentoPrenotazione === 3) {
      appointmentResearchRedirectAtConclusion();
    }

    setCalendarData({});
  };

  const refreshAppointments = () => {
    context?.setPazienteDataSingolo(undefined);
    handleSubmit(handleDataSubmit)(); // Richiama la ricerca aggiornata
  };

  // Evento invocato all'input di conclusione
  const handleConclusion = () => {
    // setOpenSummary(false);
    refreshAppointments();
    // handleReset();
    handleResetForm();
    setAccordion(true);
    // handleScrollReset();
    context?.clearAppointmentsList();
    context?.setEnte("");
  };

  // lock del tipo di agenda se viene effettuata una modifica o un ciclo di prenotazioni
  useEffect(() => {
    if (!context?.tipoAgendaLock) {
      if (typologyData) {
        if (typologyData.length > 0) {
          if (specialitiesData) {
            if (specialitiesData.length < 1) {
              setValue("tipoAgenda", typologyData[0].CODICE);
            }
          }
        }
      }
    } else {
      setValue("tipoAgenda", context?.tipoAgendaLock);
    }
  }, [
    typologyData,
    doctorData,
    specialitiesData,
    performanceData,
    setValue,
    context?.tipoAgendaLock,
  ]);

  // controllo se i dati restituiti in specialità sono pari ad uno effettua assegnazione automatica di quel valore
  useEffect(() => {
    if (specialitiesData) {
      if (specialitiesData.length === 1) {
        setValue("specialita", specialitiesData[0].SPECIALITA);
      }
    }
  }, [specialitiesData, setValue]);

  useEffect(() => {
    setValue("giorni", daysInput);
  }, [daysInput, setValue]);

  useEffect(() => {
    if (Object.keys(calendarData).length > 0) {
      setAccordion(false);
    }
  }, [calendarData]);

  const dialogBox = useRef<HTMLDivElement>(null);

  const resetScroll = () => {
    if (dialogBox.current) {
      dialogBox.current.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const handleDoubleDays = () => {
    const currentDate = getValues("data");
    if (!currentDate) return;

    const newDate = dayjs(currentDate).add(15, "day").format("YYYY-MM-DD");
    setValue("data", newDate);
    refreshAppointments();
  };

  const handleHalfDays = () => {
    const currentDate = getValues("data");
    if (!currentDate) return;

    const newDate = dayjs(currentDate).subtract(15, "day").format("YYYY-MM-DD");

    const todayDate = dayjs(today);
    const finalDate = dayjs(newDate).isBefore(todayDate)
      ? todayDate.format("YYYY-MM-DD")
      : newDate;

    setValue("data", finalDate);
    refreshAppointments();
  };

  const medicoValues = getValues("medico");

  const propsGestioneSospesi = {
    listaOrariDettaglio,
    setListaOrariDettaglio,
    modalitaSospesi,
    setModalitaSospesi,
    refreshAppointments,
  };

  if (isMobile) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          📲 Questa funzionalità non è disponibile su smartphone.
        </Typography>
        <Typography sx={{ textAlign: "center", mt: 2 }}>
          Accedi da un dispositivo con uno schermo più grande per visualizzare
          la pagina.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {!modalitaSospesi.view &&
        (context?.showOrarioGiornaliero ? (
          <>
            <AppointmentManagementDetail
              refreshAppointments={refreshAppointments}
            />
          </>
        ) : (
          <>
            <Helmet>
              <title>Ricerca disponibilità</title>
            </Helmet>
            <NotifyCancelAppointment
              message={message}
              cancellationNotification={cancellationNotification}
              cancellationModalTimer={cancellationModalTimer}
            />
            <Box ref={dialogBox} display="flex" flexDirection="column">
              {modalitaSospesi.state && (
                <AlertGestioneSospesi setModalitaSospesi={setModalitaSospesi} />
              )}
              <Accordion
                sx={{ mt: modalitaSospesi.state ? 2 : 0 }}
                onChange={() => setAccordion(!accordion)}
                className={!accordion ? "accordion" : ""}
                expanded={accordion}
              >
                <AccordionSummary
                  sx={{
                    "&:hover": {
                      borderTopRightRadius: 3,
                      borderTopLeftRadius: 3,
                      backgroundColor: "#2E83C4 !important",
                      color: "#FFFFFF !important",
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        color: "#fff",
                      },
                      "& .svg-icon path": {
                        fill: "#fff",
                      },
                    },
                  }}
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Box display={"flex"} alignItems={"center"}>
                    <Grid
                      container
                      spacing={1}
                      alignItems={"flex-end"}
                      marginLeft={0}
                    >
                      <Grid item>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="svg-icon"
                        >
                          <g clipPath="url(#clip0_521_58417)">
                            <path
                              d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                              fill="#546E7A"
                              className="svg-path"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_521_58417">
                              <rect width="24" height="24" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" component="div">
                          Ricerca Disponibilità Visite
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <form
                    className="form"
                    onSubmit={handleSubmit((data) => {
                      handleDataSubmit(data);
                      setSearchSubmit(true);
                    })}
                  >
                    <Grid container rowSpacing={1.5} columnSpacing={2.5}>
                      <Grid item xs={10} style={{ paddingTop: `0` }}>
                        <p
                          style={{
                            fontWeight: "bold",
                            color: "#00000099",
                          }}
                        >
                          Selezionare le opzioni desiderate
                        </p>
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        {!loadingDataInputs ? (
                          <Controller
                            name="tipoAgenda"
                            control={control}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <Autocomplete
                                autoComplete
                                autoHighlight
                                disabled={
                                  context?.statusAvanzamentoPrenotazione ===
                                    1 ||
                                  context?.statusAvanzamentoPrenotazione ===
                                    3 ||
                                  loadingDataInputs
                                }
                                options={typologyData || []}
                                getOptionLabel={(option) =>
                                  option.DESCRIZIONE || ""
                                }
                                onChange={(_, newValue) => {
                                  onChange(newValue ? newValue.CODICE : "");
                                }}
                                value={
                                  (typologyData || []).find(
                                    (option) => option.CODICE === value
                                  ) || null
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Tipo Agenda"
                                    variant="outlined"
                                    fullWidth
                                    helperText={error ? error.message : null}
                                  />
                                )}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            label="Tipo Agenda"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ bgcolor: "transparent", gap: 2 }}
                                >
                                  <CircularProgress size={"1rem"} />
                                  {` Caricamento.. `}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={12} md={6}>
                        {!loadingDataInputs ? (
                          <Controller
                            name="medico"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <Autocomplete
                                autoComplete
                                autoHighlight
                                multiple
                                disabled={loadingDataInputs}
                                limitTags={1}
                                options={doctorData || []}
                                getOptionLabel={(option) =>
                                  option.CODDESCR || ""
                                }
                                onChange={(_, newValue) => {
                                  onChange(
                                    newValue
                                      ? newValue.map((val) => val.CODICE)
                                      : []
                                  );
                                }}
                                value={
                                  doctorData?.filter((option) =>
                                    value?.includes(option.CODICE)
                                  ) || []
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Medico"
                                    variant="outlined"
                                    fullWidth
                                  />
                                )}
                                renderOption={(props, option) => (
                                  <MenuItem
                                    {...props}
                                    key={option.CODICE}
                                    value={option.CODICE}
                                  >
                                    {option.CODDESCR}
                                  </MenuItem>
                                )}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            label="Medico"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ bgcolor: "transparent", gap: 2 }}
                                >
                                  <CircularProgress size={"1rem"} />
                                  {` Caricamento.. `}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        display={{ sm: "none", md: "block" }}
                      ></Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        {!loadingDataInputs ? (
                          <Controller
                            rules={{ required: "Questo campo è obbligatorio" }}
                            name="specialita"
                            control={control}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <Autocomplete
                                autoComplete
                                autoHighlight
                                disabled={
                                  context?.statusAvanzamentoPrenotazione ===
                                    1 ||
                                  context?.statusAvanzamentoPrenotazione ===
                                    3 ||
                                  loadingDataInputs
                                }
                                options={specialitiesData || []}
                                getOptionLabel={(option) =>
                                  option.DESCRIZIONE || ""
                                }
                                onChange={(_, newValue) => {
                                  onChange(newValue ? newValue.SPECIALITA : "");
                                }}
                                value={
                                  (specialitiesData || []).find(
                                    (option) => option.SPECIALITA === value
                                  ) || null
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Specialità"
                                    variant="outlined"
                                    fullWidth
                                    helperText={error ? error.message : null}
                                  />
                                )}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            label="Specialità"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ bgcolor: "transparent", gap: 2 }}
                                >
                                  <CircularProgress size={"1rem"} />
                                  {` Caricamento.. `}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={12} md={6}>
                        {!loadingDataInputs ? (
                          <Controller
                            rules={{ required: "Questo campo è obbligatorio" }}
                            name="prestazione"
                            control={control}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => (
                              <Autocomplete
                                autoComplete
                                autoHighlight
                                disabled={
                                  context?.statusAvanzamentoPrenotazione ===
                                    1 ||
                                  context?.statusAvanzamentoPrenotazione ===
                                    3 ||
                                  loadingDataInputs
                                }
                                options={performanceData || []}
                                getOptionLabel={(option) =>
                                  option.DESPRESTAZIONE || ""
                                }
                                renderOption={(props, option, { index }) => (
                                  <MenuItem {...props} key={index}>
                                    {option.DESPRESTAZIONE}
                                  </MenuItem>
                                )}
                                onChange={(_, newValue) => {
                                  // aggiorno codice nel form
                                  onChange(
                                    newValue ? newValue.PRESTAZIONE : ""
                                  );
                                  // aggiorno anche l’oggetto nello state
                                  setSelectedPrestazione(newValue ?? null);
                                }}
                                value={
                                  (performanceData || []).find(
                                    (option) => option.PRESTAZIONE === value
                                  ) || null
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Prestazione"
                                    variant="outlined"
                                    fullWidth
                                    helperText={error ? error.message : null}
                                  />
                                )}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            label="Prestazione"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ bgcolor: "transparent", gap: 2 }}
                                >
                                  <CircularProgress size={"1rem"} />
                                  {` Caricamento.. `}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <p
                          style={{
                            fontWeight: "bold",
                            color: "#00000099",
                          }}
                        >
                          Indicare giornate e orari desiderati
                        </p>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        display={"flex"}
                        justifyContent={"center"}
                      >
                        <Button
                          size="small"
                          variant="containedCustomDays"
                          onClick={handleHalfDays}
                          disableElevation
                          sx={{
                            height: 56, // stessa altezza della textfield
                            width: 56,
                            minWidth: 56,
                            borderRadius: 1,
                            mr: 1,
                          }}
                        >
                          <ArrowBackIcon />
                        </Button>
                        <Controller
                          name="data"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              label="Data Inizio"
                              type="date"
                              color="secondary"
                              error={dateError}
                              fullWidth
                              inputProps={{ max: "9999-12-31", min: today }}
                              InputLabelProps={{ shrink: true }}
                              {...field}
                              value={field.value || ""}
                              required
                              onChange={(e) => {
                                field.onChange(e);
                                if (dayjs(e.target.value) < dayjs(today)) {
                                  setDateError(true);
                                } else {
                                  setDateError(false);
                                }
                                setSelectedDate(e.target.value);
                              }}
                            />
                          )}
                        />
                        <Button
                          size="small"
                          variant="containedCustomDays"
                          onClick={handleDoubleDays}
                          disableElevation
                          sx={{
                            height: 56,
                            width: 56,
                            minWidth: 56,
                            borderRadius: 1,
                            ml: 1,
                          }}
                        >
                          <ArrowForwardIcon />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Controller
                          name="giorni"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              label="Giorni"
                              type="number"
                              fullWidth
                              {...field}
                              value={daysInput === 0 ? 0 : daysInput}
                              onChange={(e) => {
                                const newDays =
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value);
                                setDaysInput(newDays);
                              }}
                              inputProps={{
                                min: 0, //Sotto lo zero non si può scendere
                              }}
                            />
                          )}
                        />
                      </Grid>
                      {hasNote(selectedPrestazione) ? (
                        <Grid item xs={12} sm={6} md={6}>
                          <Alert
                            severity="info"
                            sx={{ height: "100%", border: "1px solid #03a9f4" }}
                          >
                            <Typography fontWeight="bold" mb={0.5}>
                              Nota prestazione
                            </Typography>
                            <Typography whiteSpace="pre-wrap">
                              {selectedPrestazione!.NOTEPRENOTAZIONE}
                            </Typography>
                          </Alert>
                        </Grid>
                      ) : (
                        // placeholder per mantenere l’allineamento quando non ci sono note
                        <Grid
                          item
                          xs={false}
                          md={6}
                          display={{ xs: "none", md: "block" }}
                        />
                      )}

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{
                          marginTop: {
                            sm: 2,
                          },
                        }}
                      >
                        <Controller
                          name="orari"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              select
                              label="Orari"
                              fullWidth
                              {...field}
                              value={field.value}
                              required
                            >
                              <MenuItem value="Tutto il Giorno">
                                Tutto il Giorno
                              </MenuItem>
                              <MenuItem value="Mattina">Mattina</MenuItem>
                              <MenuItem value="Pomeriggio">Pomeriggio</MenuItem>
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{
                          marginTop: {
                            sm: 2,
                          },
                        }}
                      >
                        <Controller
                          name="strutture"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Autocomplete
                              options={[
                                { id: "", descrizione: "Tutte le Strutture" },
                                ...locationsData,
                              ]}
                              getOptionLabel={(option) =>
                                option.descrizione || ""
                              }
                              onChange={(_, newValue) => {
                                onChange(newValue ? newValue.id : "");
                              }}
                              value={
                                (locationsData || []).find(
                                  (location) => location.id === value
                                ) ||
                                (value === ""
                                  ? {
                                      id: "",
                                      descrizione: "Tutte le Strutture",
                                    }
                                  : null)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Struttura"
                                  variant="outlined"
                                  fullWidth
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                            />
                          )}
                        />
                      </Grid>
                      {/* Per visualizzare la tabella delle prenotazioni multiple */}
                      {context?.appointmentsList &&
                        context.appointmentsList.length > 0 && (
                          <Box
                            sx={{ marginTop: 2, paddingLeft: 2, width: "100%" }}
                          >
                            <Typography
                              component="div"
                              sx={{
                                color: "#003267",
                                fontWeight: "bold",
                                marginBottom: 1,
                                fontSize: "1.2rem",
                              }}
                            >
                              Prenotazioni Effettuate
                            </Typography>

                            <Paper
                              elevation={0}
                              sx={{
                                border: "1px solid #CCC",
                                borderRadius: "8px",
                                overflow: "hidden",
                              }}
                            >
                              <Grid
                                container
                                sx={{ backgroundColor: "#F3F3F3" }}
                              >
                                {/* Colonna di sinistra */}
                                <Grid
                                  item
                                  xs={12}
                                  md={3}
                                  sx={{
                                    borderRight: {
                                      md: "1px solid #E0E0E0",
                                      xs: "none",
                                    },
                                    padding: 2,
                                  }}
                                >
                                  <Typography
                                    color="#01579B"
                                    fontWeight="bold"
                                    sx={{ marginBottom: 1 }}
                                  >
                                    Prenotazione N.
                                    {appointmentDateToUpdateData?.PRENOTAZIONE ||
                                      context.numeroAppuntamento}
                                  </Typography>
                                  <Box>
                                    <Typography
                                      color="#01579B"
                                      fontWeight="bold"
                                      sx={{ fontSize: "0.9rem" }}
                                    >
                                      Paziente
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.9rem" }}>
                                      {
                                        context?.appointmentsList[0]
                                          ?.cognomePaziente
                                      }{" "}
                                      {
                                        context?.appointmentsList[0]
                                          ?.nomePaziente
                                      }
                                    </Typography>
                                  </Box>
                                </Grid>

                                {/* Colonna centrale */}
                                <Grid
                                  item
                                  xs={12}
                                  md={3}
                                  sx={{
                                    borderRight: {
                                      md: "1px solid #E0E0E0",
                                      xs: "none",
                                    },
                                    padding: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  {context?.appointmentsList &&
                                    context.appointmentsList.map(
                                      (appointment, index) => {
                                        const previous =
                                          context.appointmentsList[index - 1];

                                        const isDifferentFromPrevious =
                                          !previous ||
                                          previous.descrizionePrestazione !==
                                            appointment.descrizionePrestazione;

                                        if (isDifferentFromPrevious) {
                                          return (
                                            <>
                                              <Typography
                                                sx={{ fontSize: "0.95rem" }}
                                              >
                                                {
                                                  appointment.descrizionePrestazione
                                                }
                                              </Typography>
                                              <Typography
                                                sx={{ fontSize: "0.9rem" }}
                                              >
                                                <strong>Dr.</strong>{" "}
                                                {appointment.nominativoMedico}
                                              </Typography>
                                            </>
                                          );
                                        } else {
                                          return <></>;
                                        }
                                      }
                                    )}
                                </Grid>

                                {/* Colonna di destra */}
                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  sx={{ padding: 2, width: "100%" }}
                                >
                                  {context?.appointmentsList.map(
                                    (appointment, idx) => {
                                      if (
                                        appointment.statoPrenotazione ===
                                          "Attiva" ||
                                        !appointment.statoPrenotazione
                                      ) {
                                        return (
                                          <CustomTypography
                                            key={idx}
                                            idx={idx}
                                            appointment={appointment}
                                            appointmentDateToUpdateData={
                                              appointmentDateToUpdateData
                                            }
                                          />
                                        );
                                      }
                                      return <></>;
                                    }
                                  )}
                                </Grid>
                              </Grid>
                            </Paper>
                          </Box>
                        )}
                      <Grid item xs={12} sm={12} md={12}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                              variant="outlinedCustom"
                              disabled={
                                context?.statusAvanzamentoPrenotazione === 0 ||
                                context?.statusAvanzamentoPrenotazione === 2
                                  ? false
                                  : true
                              }
                              onClick={handleResetFields}
                            >
                              Reset Filtri
                            </Button>
                            {context?.statusAvanzamentoPrenotazione !== 0 && (
                              <Button
                                variant="contained"
                                className="button-new-search"
                                onClick={handleConclusion}
                              >
                                Concludi
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            {!modalitaSospesi.state && (
                              <Button
                                variant="outlinedCustom"
                                onClick={() =>
                                  setModalitaSospesi((prev) => ({
                                    ...prev,
                                    state: true,
                                  }))
                                }
                              >
                                Gestione Sospesi
                              </Button>
                            )}
                            <Button
                              type="submit"
                              variant="contained"
                              className="button-new-search"
                              disabled={isDisabled}
                            >
                              Ricerca Disponibilità
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </AccordionDetails>
              </Accordion>

              {!loadingData && Object.keys(calendarData).length === 0 && (
                <>
                  {!loadingData &&
                    searchSubmit &&
                    Object.keys(calendarData).length === 0 && (
                      <Box>
                        <Grid>
                          <Grid item xs={12} lg={12} xl={12}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "3em",
                                fontSize: "1.1rem",
                                borderRadius: "8px",
                                marginTop: 2,
                                textAlign: "center",
                                backgroundColor: "#FFDF77",
                              }}
                            >
                              <Typography variant="body1">
                                La ricerca non ha prodotto risultati
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: "0.3",
                    }}
                  >
                    <svg
                      width="552.5"
                      height="442"
                      viewBox="0 0 250 200"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M207 65C210.866 65 214 68.134 214 72C214 75.866 210.866 79 207 79H167C170.866 79 174 82.134 174 86C174 89.866 170.866 93 167 93H189C192.866 93 196 96.134 196 100C196 103.866 192.866 107 189 107H178.826C173.952 107 170 110.134 170 114C170 116.577 172 118.911 176 121C179.866 121 183 124.134 183 128C183 131.866 179.866 135 176 135H93C89.134 135 86 131.866 86 128C86 124.134 89.134 121 93 121H54C50.134 121 47 117.866 47 114C47 110.134 50.134 107 54 107H94C97.866 107 101 103.866 101 100C101 96.134 97.866 93 94 93H69C65.134 93 62 89.866 62 86C62 82.134 65.134 79 69 79H109C105.134 79 102 75.866 102 72C102 68.134 105.134 65 109 65H207ZM207 93C210.866 93 214 96.134 214 100C214 103.866 210.866 107 207 107C203.134 107 200 103.866 200 100C200 96.134 203.134 93 207 93Z"
                        fill="#8DBEE94D"
                      />
                      <path
                        d="M120.5 133C139.002 133 154 118.002 154 99.5C154 80.9985 139.002 66 120.5 66C101.998 66 87 80.9985 87 99.5C87 118.002 101.998 133 120.5 133Z"
                        fill="none"
                        stroke="#2E83C4"
                        strokeWidth="2.5"
                      />
                      <path
                        d="M115.132 125.494C116.891 125.819 118.68 125.987 120.5 126C135.136 126 147 114.136 147 99.5C147 84.8645 135.136 73 120.5 73C116.74 73 113.164 73.7829 109.924 75.1946C104.294 77.6479 99.6816 81.9999 96.896 87.4419C95.0445 91.0589 94 95.1575 94 99.5C94 103.44 94.8599 107.179 96.4021 110.54C97.5032 112.94 98.9521 115.146 100.684 117.096"
                        stroke="#2E83C4"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M103.797 120.075C105.945 121.821 108.372 123.237 111.001 124.247"
                        stroke="#2E83C4"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M148 126L154 132"
                        stroke="#2E83C4"
                        strokeWidth="2.5"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M153.03 131.03C151.138 132.923 151.138 135.992 153.03 137.885L164.116 148.97C166.008 150.863 169.077 150.863 170.97 148.97C172.863 147.077 172.863 144.008 170.97 142.116L159.885 131.03C157.992 129.138 154.923 129.138 153.03 131.03Z"
                        fill="#56A6EF80"
                        stroke="#2E83C4"
                        strokeWidth="2.5"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M114 88C114 99.598 123.402 109 135 109C137.278 109 139.472 108.637 141.526 107.966C138.173 116.287 130.023 122.161 120.5 122.161C107.985 122.161 97.8394 112.015 97.8394 99.5C97.8394 88.1596 106.17 78.7648 117.045 77.1011C115.113 80.2793 114 84.0097 114 88Z"
                        fill="#56A6EF80"
                      />
                      <path
                        d="M121 81C119.727 81 118.482 81.1253 117.279 81.3642M113.645 82.4761C106.804 85.3508 102 92.1144 102 100"
                        stroke="#56A6EF"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M180.5 92H163.324M187.5 92H185.279M174.176 99.7773H166"
                        stroke="#56A6EF"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M84.1758 121.777H76M79.5 113H62.3242H79.5ZM56.5 113H52.2788H56.5Z"
                        stroke="#56A6EF"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                </>
              )}

              {/*
               * Aggiunto loading spinner poiché qualche logica di refresh agganciate a
               * loadingData impediva di aggiornare lo state su Calendar direttamente tramite quello
               */}
              {(loadingData || loadingSpinner) && (
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 1300,
                  }}
                >
                  <CircularProgress size="6rem" />
                </Box>
              )}

              {!loadingData && Object.keys(calendarData).length > 0 && (
                <Grid container spacing={2} /*maxWidth="1038px"*/>
                  {Object.keys(calendarData).map((medicoId, index) => {
                    // console.log({index});
                    // console.log({medicoId});

                    return Object.keys(calendarData[medicoId]).map(
                      (agendaKey, index) => {
                        // console.log({index});
                        // console.log({agendaKey});

                        const [tipoAgenda, codice] = agendaKey.split("-");
                        const agenda = calendarData[medicoId][agendaKey];

                        return (
                          // logica che mostra i risultati in base al filtro di inserimento di un medico e non renderizza componente in caso *medicoValues* risulti vuota
                          ((typeof medicoValues === "string" &&
                            medicoValues === "" &&
                            agenda.appuntamenti.length > 0) ||
                            (Array.isArray(medicoValues) &&
                              medicoValues.length === 1 &&
                              medicoValues[0] === "" &&
                              agenda.appuntamenti.length > 0) ||
                            (typeof medicoValues === "string" &&
                              medicoValues === medicoId &&
                              agenda.appuntamenti.length > 0) ||
                            (Array.isArray(medicoValues) &&
                              agenda.appuntamenti.length > 0 &&
                              (medicoValues.length === 0 ||
                                medicoValues.includes(medicoId)))) && (
                            <Grid
                              item
                              xs={12}
                              md={12}
                              key={`${medicoId}-${agendaKey}`}
                            >
                              <Calendar
                                generateID={generateId}
                                onBookingComplete={refreshAppointments}
                                handleResetForm={handleResetForm}
                                handleCancellationNotification={
                                  handleCancellationNotification
                                }
                                handleScrollReset={resetScroll}
                                setCancellationNotification={
                                  setCancellationNotification
                                }
                                setSearchSubmit={setSearchSubmit}
                                setCancellationModalTimer={
                                  setCancellationModalTimer
                                }
                                loadingSpinner={loadingSpinner}
                                setLoadingSpinner={setLoadingSpinner}
                                giorni={daysForCalendar}
                                searchedDate={selectedDate}
                                primoAppuntamento={firstDateAvailable}
                                setAccordion={setAccordion}
                                listaOrariDettaglio={listaOrariDettaglio}
                                setListaOrariDettaglio={setListaOrariDettaglio}
                                calendarData={agenda.appuntamenti.map(
                                  (appuntamento) => {
                                    // console.log({appuntamento});

                                    return {
                                      title: `${agenda.nominativoMedico} - ${agenda.struttura} - ${agenda.tariffa}`,
                                      start: `${appuntamento.data}T${appuntamento.orari}`,
                                      medicoId,
                                      nominativoMedico: agenda.nominativoMedico,
                                      struttura: agenda.struttura,
                                      sede: agenda.sede,
                                      sedeIndirizzo: agenda.sedeIndirizzo,
                                      tariffa: agenda.tariffa,
                                      durata: appuntamento.durataPrestazione,
                                      genere: agenda.genere,
                                      prestazione: appuntamento.prestazione,
                                      specialita: appuntamento.specialita,
                                      tipoAgenda: appuntamento.tipoAgenda,
                                      agenda: appuntamento.codice,
                                      codice: agenda.codice,
                                      primaDisponibilita:
                                        appuntamento.primaDisponibilita,
                                      ora: appuntamento.orari,
                                      tipologiaAgenda:
                                        appuntamento.tipologiaAgenda,
                                      selectedDate: selectedDate,
                                      durataPrestazione:
                                        appuntamento.durataPrestazione,
                                      descrizionePrestazione:
                                        appuntamento.descrizionePrestazione,
                                      descrizioneSpecialita:
                                        appuntamento.descrizioneSpecialita,
                                      descrizioneTipoAgenda:
                                        appuntamento.descrizioneTipoAgenda,
                                      noteSettimana: agenda.noteSettimana,
                                      notaMedico: agenda.notaMedico,
                                      flagOrarioGiornaliero:
                                        appuntamento.flagOrarioGiornaliero,
                                    };
                                  }
                                )}
                                rowsEntiData={rowsEntiData}
                                rowsPosizioneTicketData={
                                  rowsPosizioneTicketData
                                }
                                rowsClassePrioritaData={rowsClassePrioritaData}
                                message={message}
                                setMessage={setMessage}
                                modalitaSospesi={modalitaSospesi}
                                setModalitaSospesi={setModalitaSospesi}
                              />
                            </Grid>
                          )
                        );
                      }
                    );
                  })}
                </Grid>
              )}
            </Box>
          </>
        ))}
      {modalitaSospesi.view && (
        <Grid container sx={{ display: "flex" }}>
          <Grid item xs={12} mb={2}>
            <AlertGestioneSospesi setModalitaSospesi={setModalitaSospesi} />
          </Grid>
          <Grid item xs={12}>
            <GestioneSospesi {...propsGestioneSospesi} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AppointmentManagement;
